import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { telegramService } from '../../services/telegram.service'
import TelegramSetup from '../onboarding/TelegramSetup'
import toast from 'react-hot-toast'

export default function TelegramSettings() {
  const queryClient = useQueryClient()

  const { data: status, isLoading } = useQuery({
    queryKey: ['telegramStatus'],
    queryFn: () => telegramService.getStatus(),
  })

  const disconnectMutation = useMutation({
    mutationFn: () => telegramService.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telegramStatus'] })
      toast.success('Telegram отключён')
    },
    onError: () => {
      toast.error('Ошибка при отключении Telegram')
    },
  })

  const handleDisconnect = () => {
    if (window.confirm('Вы уверены, что хотите отключить Telegram?')) {
      disconnectMutation.mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="card flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // Not connected - show setup wizard
  if (!status?.connected) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Подключение Telegram
        </h2>
        <p className="text-gray-600 mb-6">
          Подключите ваш аккаунт Telegram для мониторинга каналов и отправки сообщений.
        </p>
        <TelegramSetup />
      </div>
    )
  }

  // Connected - show status
  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Статус Telegram
        </h2>

        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
            <svg
              className="h-6 w-6 text-blue-600"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
            </svg>
          </div>
          <div>
            <p className="font-medium text-gray-900">
              {status.phone_number || 'Telegram аккаунт'}
            </p>
            <p className="text-sm text-green-600">✓ Подключён</p>
          </div>
        </div>

        {/* Connection Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Информация о подключении
          </h3>
          <div className="space-y-1 text-sm">
            {status.phone_number && (
              <p>
                <span className="text-gray-500">Номер:</span>{' '}
                <span className="text-gray-900">{status.phone_number}</span>
              </p>
            )}
            {status.session_created_at && (
              <p>
                <span className="text-gray-500">Подключён:</span>{' '}
                <span className="text-gray-900">
                  {new Date(status.session_created_at).toLocaleDateString('ru-RU')}
                </span>
              </p>
            )}
          </div>
        </div>

        {/* Disconnect Button */}
        <button
          onClick={handleDisconnect}
          disabled={disconnectMutation.isPending}
          className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
        >
          {disconnectMutation.isPending ? 'Отключение...' : 'Отключить Telegram'}
        </button>
      </div>

      {/* Security Notice */}
      <div className="card bg-blue-50 border-blue-200">
        <h3 className="text-sm font-medium text-blue-900 mb-2">
          🔒 Безопасность
        </h3>
        <p className="text-sm text-blue-800">
          Ваши данные Telegram зашифрованы и хранятся в безопасном хранилище.
          Мы никогда не передаём ваши данные третьим лицам.
        </p>
      </div>
    </div>
  )
}
